/**
 * ImportsDashboard — per-vendor status table + data-health panel +
 * schedule monitor. Read-only. Lives alongside the existing ImportsList
 * page (which still drives CSV uploads + vendor-pull buttons).
 *
 * Backend endpoints powering this page (see src/routes/admin/dashboard.ts):
 *   GET /admin/imports/sources    → per-vendor status table
 *   GET /admin/data-health        → cross-vendor health checks
 *   GET /admin/imports/schedules  → EventBridge cron rules + state
 */
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Badge,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Divider,
  EmptyState,
} from '@shopify/polaris';
import {
  listImportSources,
  getDataHealth,
  listImportSchedules,
  type SourceStatus,
  type SourceRow,
  type HealthCheck,
} from '../api/imports';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return iso;
  }
}

function statusTone(s: SourceStatus): 'success' | 'attention' | 'critical' | 'info' | 'warning' {
  switch (s) {
    case 'OK':
      return 'success';
    case 'Running':
      return 'info';
    case 'Needs Attention':
      return 'attention';
    case 'Failed':
      return 'critical';
    case 'Never Run':
      return 'info';
    case 'Image Blocked':
      return 'warning';
  }
}

function severityTone(s: HealthCheck['severity']): 'critical' | 'warning' | 'info' {
  switch (s) {
    case 'critical':
      return 'critical';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
  }
}

export default function ImportsDashboard() {
  const sourcesQ = useQuery({
    queryKey: ['imports', 'sources'],
    queryFn: listImportSources,
    refetchInterval: 30_000,
  });
  const healthQ = useQuery({
    queryKey: ['imports', 'health'],
    queryFn: getDataHealth,
    refetchInterval: 60_000,
  });
  const schedQ = useQuery({
    queryKey: ['imports', 'schedules'],
    queryFn: listImportSchedules,
    refetchInterval: 120_000,
  });

  const sourcesRows: (SourceRow & { _renderedStatus: JSX.Element })[] =
    (sourcesQ.data?.data ?? []).map((r) => ({
      ...r,
      _renderedStatus: <Badge tone={statusTone(r.status)}>{r.status}</Badge>,
    }));

  return (
    <Page title="Imports dashboard" subtitle="Vendor status, data health, and scheduled jobs">
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Sources
              </Text>
              {sourcesQ.isLoading ? (
                <Text as="p">Loading…</Text>
              ) : sourcesQ.isError ? (
                <Banner tone="critical" title="Failed to load sources">
                  {(sourcesQ.error as Error).message}
                </Banner>
              ) : sourcesRows.length === 0 ? (
                <EmptyState heading="No sources configured" image="">
                  <p>The /admin/imports/sources endpoint returned an empty list.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={[
                    'text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'text',
                  ]}
                  headings={[
                    'Source', 'Type', 'Last Catalog', 'Last Inventory Sync',
                    'DB Rows', 'Ready', 'Pushed', 'Blocked', 'Image Missing', 'Status',
                  ]}
                  rows={sourcesRows.map((r) => [
                    r.source,
                    r.productType,
                    formatDate(r.lastCatalogImport),
                    formatDate(r.lastInventorySync),
                    r.dbRows,
                    r.ready,
                    r.pushed,
                    r.blocked,
                    r.imageMissing,
                    r._renderedStatus,
                  ])}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Data health
                </Text>
                {healthQ.data?.generatedAt ? (
                  <Text as="span" variant="bodySm" tone="subdued">
                    Generated {formatDate(healthQ.data.generatedAt)}
                  </Text>
                ) : null}
              </InlineStack>
              {healthQ.isLoading ? (
                <Text as="p">Loading…</Text>
              ) : healthQ.isError ? (
                <Banner tone="critical" title="Failed to load health checks">
                  {(healthQ.error as Error).message}
                </Banner>
              ) : (
                <BlockStack gap="300">
                  {(healthQ.data?.checks ?? []).map((c) => (
                    <Banner
                      key={c.id}
                      tone={severityTone(c.severity)}
                      title={`${c.title} — ${c.count}`}
                    >
                      <BlockStack gap="100">
                        <Text as="p">{c.detail}</Text>
                        {c.sample.length > 0 ? (
                          <Text as="p" variant="bodySm" tone="subdued">
                            Sample: {c.sample.slice(0, 5).join(', ')}
                          </Text>
                        ) : null}
                      </BlockStack>
                    </Banner>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Scheduled jobs
              </Text>
              {schedQ.isLoading ? (
                <Text as="p">Loading…</Text>
              ) : schedQ.data?.note ? (
                <Banner tone="info" title="Schedule data unavailable">
                  {schedQ.data.note}
                </Banner>
              ) : (schedQ.data?.data ?? []).length === 0 ? (
                <Text as="p">No scheduled jobs found.</Text>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['Rule', 'Schedule (UTC)', 'State', 'Description']}
                  rows={(schedQ.data?.data ?? []).map((r) => [
                    r.name ?? '—',
                    r.schedule ?? '—',
                    <Badge key={r.name} tone={r.state === 'ENABLED' ? 'success' : 'attention'}>
                      {r.state ?? 'UNKNOWN'}
                    </Badge>,
                    r.description ?? '',
                  ])}
                />
              )}
              <Divider />
              <Text as="p" variant="bodySm" tone="subdued">
                Cron expressions are in UTC. Atlanta ET = UTC −4 (EDT) or −5 (EST).
                Daily inventory sync at 9 UTC = 5 AM EDT / 4 AM EST.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
